import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Sparkles, FileText, Layers, Video, Loader2, Copy, 
  Bookmark, BookmarkCheck, ChevronRight, Image as ImageIcon,
  Instagram, Linkedin, Twitter, Youtube, Zap, Download, AlertTriangle, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { CaptionLengthPicker } from '../components/CaptionLengthPicker';
import { AdvancedPromptDetails } from '../components/AdvancedPromptDetails';
import { StyleProfileSelector } from '../components/StyleProfileSelector';
import { FunnelCtaPicker, buildTrackedUrl } from '../components/FunnelCtaPicker';

const TONES = ['Professional', 'Educational', 'Motivational', 'Promotional', 'Casual'];
const PLATFORMS = [
  { id: 'Instagram', label: 'Instagram', icon: <Instagram className="w-4 h-4" /> },
  { id: 'LinkedIn', label: 'LinkedIn', icon: <Linkedin className="w-4 h-4" /> },
  { id: 'Bluesky', label: 'Bluesky', icon: <Twitter className="w-4 h-4" /> },
  { id: 'Facebook', label: 'Facebook', icon: <div className="text-xs font-bold">FB</div> },
  { id: 'YouTube Shorts', label: 'YouTube Shorts', icon: <Youtube className="w-4 h-4" /> }
];

export const GeneratorPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('type') || 'posts';
  const { api, token } = useAuth();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState([]);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Bulk generation state
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkStats, setBulkStats] = useState({ generated: 0, total: 0 });
  const eventSourceRef = useRef(null);

  // Fetch subscription info on mount
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get('/subscription/current');
        setSubscription(response.data);
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      }
    };
    fetchSubscription();
  }, [api]);

  // Posts form state
  const [postsForm, setPostsForm] = useState({
    topic: '',
    audience: '',
    tone: 'Professional',
    platforms: ['Instagram'],
    numPosts: 5,
    // captionLength: { mode: 'auto' | 'fixed' | 'per_platform', words?: number }
    captionLength: { mode: 'auto' },
    styleExample: '',
    extraInstructions: '',
  });
  const [ctaFunnel, setCtaFunnel] = useState(null);

  // Carousel form state
  const [carouselForm, setCarouselForm] = useState({
    topic: '',
    audience: '',
    tone: 'Professional',
    platform: 'Instagram',
    numSlides: 5
  });

  // Video form state
  const [videoForm, setVideoForm] = useState({
    topic: '',
    audience: '',
    tone: 'Professional',
    platform: 'Instagram',
    duration: 30
  });

  // Load template data from URL params
  useEffect(() => {
    const templateId = searchParams.get('template');
    if (templateId) {
      const topic = searchParams.get('topic') || '';
      const audience = searchParams.get('audience') || '';
      const tone = searchParams.get('tone') || 'Professional';
      const platforms = searchParams.get('platforms')?.split(',') || ['Instagram'];
      const posts = parseInt(searchParams.get('posts') || '5');

      // Templates can carry a pre-loaded Style Profile (stashed in
      // sessionStorage by TemplatesPage). Pop it now and merge into form so
      // styleExample, extraInstructions, and captionLength all auto-apply.
      let styleExample = '';
      let extraInstructions = '';
      let captionLength = { mode: 'auto' };
      try {
        const raw = sessionStorage.getItem('pending_style_profile');
        if (raw) {
          const p = JSON.parse(raw);
          styleExample = p.style_example || '';
          extraInstructions = p.extra_instructions || '';
          captionLength =
            p.caption_length_mode === 'fixed' && p.caption_length_words
              ? { mode: 'fixed', words: p.caption_length_words }
              : p.caption_length_mode === 'per_platform'
                ? { mode: 'per_platform' }
                : { mode: 'auto' };
          if (p.name) toast.success(`Style profile applied: ${p.name}`);
          sessionStorage.removeItem('pending_style_profile');
        }
      } catch (_) { /* ignore */ }

      setPostsForm({
        topic,
        audience,
        tone,
        platforms,
        numPosts: Math.min(posts, 50),
        captionLength,
        styleExample,
        extraInstructions,
      });

      // Enable bulk mode if suggested posts > 20
      if (posts > 20) {
        setBulkMode(true);
        setPostsForm(prev => ({ ...prev, numPosts: posts }));
      }
    }
  }, [searchParams]);

  const handlePlatformToggle = (platformId) => {
    setPostsForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const handleGeneratePosts = async () => {
    if (!postsForm.topic || !postsForm.audience) {
      toast.error('Please fill in topic and audience');
      return;
    }
    if (postsForm.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    // Check subscription limits before generating
    if (subscription) {
      const postsRemaining = subscription.posts_limit === -1 
        ? Infinity 
        : subscription.posts_limit - subscription.posts_used;
      
      if (postsRemaining <= 0) {
        setShowUpgradeModal(true);
        return;
      }
      
      if (postsForm.numPosts > postsRemaining) {
        toast.error(`You can only generate ${postsRemaining} more posts this month. Upgrade for more!`);
        setShowUpgradeModal(true);
        return;
      }
    }

    // Use bulk mode for large number of posts
    if (bulkMode && postsForm.numPosts > 20) {
      handleBulkGenerate();
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/generate/posts', {
        topic: postsForm.topic,
        audience: postsForm.audience,
        tone: postsForm.tone,
        platforms: postsForm.platforms,
        num_posts: postsForm.numPosts,
        target_words: postsForm.captionLength.mode === 'fixed' ? postsForm.captionLength.words : null,
        per_platform_lengths: postsForm.captionLength.mode === 'per_platform',
        style_example: postsForm.styleExample?.trim() || null,
        extra_instructions: postsForm.extraInstructions?.trim() || null,
        cta_url: ctaFunnel ? buildTrackedUrl(ctaFunnel, 'generate') : null,
        cta_label: ctaFunnel ? ctaFunnel.title : null,
      });
      setGeneratedContent(response.data.posts);
      // Refresh subscription data
      const subRes = await api.get('/subscription/current');
      setSubscription(subRes.data);
      toast.success(`Generated ${response.data.count} posts!`);
    } catch (error) {
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      }
      toast.error(error.response?.data?.detail || 'Failed to generate posts');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerate = () => {
    if (!postsForm.topic || !postsForm.audience) {
      toast.error('Please fill in topic and audience');
      return;
    }

    // Check subscription limits before bulk generating
    if (subscription) {
      const postsRemaining = subscription.posts_limit === -1 
        ? Infinity 
        : subscription.posts_limit - subscription.posts_used;
      
      if (postsRemaining <= 0) {
        setShowUpgradeModal(true);
        return;
      }
      
      if (postsForm.numPosts > postsRemaining) {
        toast.error(`You can only generate ${postsRemaining} more posts this month. Upgrade for more!`);
        setShowUpgradeModal(true);
        return;
      }
    }

    setBulkGenerating(true);
    setBulkProgress(0);
    setBulkStats({ generated: 0, total: postsForm.numPosts });
    setGeneratedContent([]);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams({
      topic: postsForm.topic,
      audience: postsForm.audience,
      tone: postsForm.tone,
      platforms: postsForm.platforms.join(','),
      num_posts: postsForm.numPosts.toString()
    });
    if (postsForm.captionLength.mode === 'fixed' && postsForm.captionLength.words) {
      params.set('target_words', String(postsForm.captionLength.words));
    }
    if (postsForm.captionLength.mode === 'per_platform') {
      params.set('per_platform_lengths', 'true');
    }
    if (ctaFunnel) {
      params.set('cta_url', buildTrackedUrl(ctaFunnel, 'generate'));
      params.set('cta_label', ctaFunnel.title);
    }
    // NOTE: SSE bulk streaming doesn't accept `style_example` / `extra_instructions`
    // because they can easily exceed the URL-length limit. Users who want style
    // reference posts should generate in non-bulk mode (single batch / POST).

    // Create EventSource with auth header workaround
    const url = `${backendUrl}/api/generate/bulk/stream?${params.toString()}`;
    
    // Use fetch with streaming for authenticated SSE
    const fetchStream = async () => {
      try {
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Check for 403 error
        if (response.status === 403) {
          const errorData = await response.json();
          toast.error(errorData.detail || 'Post limit reached');
          setShowUpgradeModal(true);
          setBulkGenerating(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'progress') {
                  setBulkProgress(data.progress);
                  setBulkStats({ generated: data.generated, total: data.total });
                  setGeneratedContent(prev => [...prev, ...data.posts]);
                } else if (data.type === 'complete') {
                  // Refresh subscription data
                  const subRes = await api.get('/subscription/current');
                  setSubscription(subRes.data);
                  toast.success(`Generated ${data.total_generated} posts!`);
                  setBulkGenerating(false);
                } else if (data.type === 'error') {
                  if (data.message?.includes('limit')) {
                    setShowUpgradeModal(true);
                  }
                  toast.error(data.message);
                  setBulkGenerating(false);
                }
              } catch (e) {
                // Ignore parse errors for incomplete chunks
              }
            }
          }
        }
      } catch (error) {
        console.error('Stream error:', error);
        toast.error('Bulk generation failed');
        setBulkGenerating(false);
      }
    };

    fetchStream();
  };

  const cancelBulkGenerate = () => {
    setBulkGenerating(false);
    toast.info('Generation cancelled');
  };

  const handleGenerateCarousel = async () => {
    if (!carouselForm.topic || !carouselForm.audience) {
      toast.error('Please fill in topic and audience');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/generate/carousel', {
        topic: carouselForm.topic,
        audience: carouselForm.audience,
        tone: carouselForm.tone,
        platform: carouselForm.platform,
        num_slides: carouselForm.numSlides
      });
      setGeneratedContent([response.data]);
      toast.success('Carousel generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate carousel');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!videoForm.topic || !videoForm.audience) {
      toast.error('Please fill in topic and audience');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/generate/video-script', {
        topic: videoForm.topic,
        audience: videoForm.audience,
        tone: videoForm.tone,
        platform: videoForm.platform,
        duration_seconds: videoForm.duration
      });
      setGeneratedContent([response.data]);
      toast.success('Video script generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate script');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const toggleSave = async (post) => {
    try {
      await api.put(`/posts/${post.id}`, { is_saved: !post.is_saved });
      setGeneratedContent(prev =>
        prev.map(p => p.id === post.id ? { ...p, is_saved: !p.is_saved } : p)
      );
      toast.success(post.is_saved ? 'Removed from saved' : 'Saved!');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-8rem)]" data-testid="generator-page">
      {/* Left Panel - Input Form */}
      <div className="w-full lg:w-[400px] flex-shrink-0">
        <Card className="border-zinc-200 h-full overflow-auto">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              Content Generator
            </CardTitle>
            <CardDescription>Fill in the details to generate AI content</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-3 mb-6">
                <TabsTrigger value="posts" className="text-xs sm:text-sm" data-testid="tab-posts">
                  <FileText className="w-4 h-4 mr-1.5" />
                  Posts
                </TabsTrigger>
                <TabsTrigger value="carousel" className="text-xs sm:text-sm" data-testid="tab-carousel">
                  <Layers className="w-4 h-4 mr-1.5" />
                  Carousel
                </TabsTrigger>
                <TabsTrigger value="video" className="text-xs sm:text-sm" data-testid="tab-video">
                  <Video className="w-4 h-4 mr-1.5" />
                  Video
                </TabsTrigger>
              </TabsList>

              {/* Posts Tab */}
              <TabsContent value="posts" className="space-y-4 mt-0">
                {/* Usage Indicator */}
                {subscription && (
                  <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-500">Posts remaining</span>
                      <span className="font-medium text-zinc-900">
                        {subscription.posts_limit === -1 
                          ? '∞ (Unlimited)' 
                          : `${Math.max(0, subscription.posts_limit - subscription.posts_used)} of ${subscription.posts_limit}`
                        }
                      </span>
                    </div>
                    {subscription.posts_limit > 0 && (
                      <Progress 
                        value={(subscription.posts_used / subscription.posts_limit) * 100} 
                        className="h-1.5 mt-2" 
                      />
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Topic *</Label>
                  <Input
                    placeholder="e.g., Real Estate Marketing"
                    value={postsForm.topic}
                    onChange={(e) => setPostsForm({ ...postsForm, topic: e.target.value })}
                    data-testid="posts-topic-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Target Audience *</Label>
                  <Input
                    placeholder="e.g., First-time homebuyers"
                    value={postsForm.audience}
                    onChange={(e) => setPostsForm({ ...postsForm, audience: e.target.value })}
                    data-testid="posts-audience-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <Select value={postsForm.tone} onValueChange={(v) => setPostsForm({ ...postsForm, tone: v })}>
                    <SelectTrigger data-testid="posts-tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TONES.map(tone => (
                        <SelectItem key={tone} value={tone}>{tone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Platforms</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {PLATFORMS.map(platform => (
                      <div
                        key={platform.id}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                          postsForm.platforms.includes(platform.id)
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                        onClick={() => handlePlatformToggle(platform.id)}
                        data-testid={`platform-${platform.id.toLowerCase().replace(/[^a-z]/g, '')}`}
                      >
                        <Checkbox
                          checked={postsForm.platforms.includes(platform.id)}
                          className="pointer-events-none"
                        />
                        {platform.icon}
                        <span className="text-sm">{platform.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bulk Mode Toggle */}
                <div className="p-3 rounded-lg bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-sm">Bulk Generation</span>
                    </div>
                    <Button
                      variant={bulkMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBulkMode(!bulkMode)}
                      className={bulkMode ? 'bg-indigo-600 hover:bg-indigo-700 h-7' : 'h-7'}
                      data-testid="bulk-mode-toggle"
                    >
                      {bulkMode ? 'ON' : 'OFF'}
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500">
                    {bulkMode 
                      ? 'Generate up to 365 posts with real-time progress' 
                      : 'Enable to generate large batches of content'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Number of Posts: {postsForm.numPosts}</Label>
                  <Input
                    type="range"
                    min="1"
                    max={bulkMode ? 365 : 50}
                    value={postsForm.numPosts}
                    onChange={(e) => setPostsForm({ ...postsForm, numPosts: parseInt(e.target.value) })}
                    className="cursor-pointer"
                    data-testid="posts-count-slider"
                  />
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>1</span>
                    <span>{bulkMode ? '365' : '50'}</span>
                  </div>
                </div>

                <CaptionLengthPicker
                  value={postsForm.captionLength}
                  onChange={(v) => setPostsForm({ ...postsForm, captionLength: v })}
                />

                <StyleProfileSelector
                  currentStyleExample={postsForm.styleExample}
                  currentExtraInstructions={postsForm.extraInstructions}
                  currentCaptionLength={postsForm.captionLength}
                  onApply={({ styleExample, extraInstructions, captionLength }) =>
                    setPostsForm({
                      ...postsForm,
                      styleExample,
                      extraInstructions,
                      captionLength,
                    })
                  }
                />

                <AdvancedPromptDetails
                  styleExample={postsForm.styleExample}
                  onStyleExampleChange={(v) => setPostsForm({ ...postsForm, styleExample: v })}
                  extraInstructions={postsForm.extraInstructions}
                  onExtraInstructionsChange={(v) => setPostsForm({ ...postsForm, extraInstructions: v })}
                />

                <FunnelCtaPicker
                  value={ctaFunnel?.id || null}
                  onChange={setCtaFunnel}
                  refSource="generate"
                  label="Add a lead-capture CTA (optional)"
                />

                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleGeneratePosts}
                  disabled={loading || bulkGenerating}
                  data-testid="generate-posts-btn"
                >
                  {loading || bulkGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : bulkMode ? (
                    <Zap className="w-4 h-4 mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {bulkMode ? `Generate ${postsForm.numPosts} Posts` : `Generate ${postsForm.numPosts} Posts`}
                </Button>
              </TabsContent>

              {/* Carousel Tab */}
              <TabsContent value="carousel" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label>Topic *</Label>
                  <Input
                    placeholder="e.g., 5 Ways to Improve Marketing"
                    value={carouselForm.topic}
                    onChange={(e) => setCarouselForm({ ...carouselForm, topic: e.target.value })}
                    data-testid="carousel-topic-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Target Audience *</Label>
                  <Input
                    placeholder="e.g., Small business owners"
                    value={carouselForm.audience}
                    onChange={(e) => setCarouselForm({ ...carouselForm, audience: e.target.value })}
                    data-testid="carousel-audience-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <Select value={carouselForm.tone} onValueChange={(v) => setCarouselForm({ ...carouselForm, tone: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TONES.map(tone => (
                        <SelectItem key={tone} value={tone}>{tone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select value={carouselForm.platform} onValueChange={(v) => setCarouselForm({ ...carouselForm, platform: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.slice(0, 2).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Number of Slides: {carouselForm.numSlides}</Label>
                  <Input
                    type="range"
                    min="3"
                    max="10"
                    value={carouselForm.numSlides}
                    onChange={(e) => setCarouselForm({ ...carouselForm, numSlides: parseInt(e.target.value) })}
                    className="cursor-pointer"
                  />
                </div>
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleGenerateCarousel}
                  disabled={loading}
                  data-testid="generate-carousel-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layers className="w-4 h-4 mr-2" />}
                  Generate Carousel
                </Button>
              </TabsContent>

              {/* Video Tab */}
              <TabsContent value="video" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label>Topic *</Label>
                  <Input
                    placeholder="e.g., Quick Marketing Tips"
                    value={videoForm.topic}
                    onChange={(e) => setVideoForm({ ...videoForm, topic: e.target.value })}
                    data-testid="video-topic-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Target Audience *</Label>
                  <Input
                    placeholder="e.g., Entrepreneurs"
                    value={videoForm.audience}
                    onChange={(e) => setVideoForm({ ...videoForm, audience: e.target.value })}
                    data-testid="video-audience-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <Select value={videoForm.tone} onValueChange={(v) => setVideoForm({ ...videoForm, tone: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TONES.map(tone => (
                        <SelectItem key={tone} value={tone}>{tone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select value={videoForm.platform} onValueChange={(v) => setVideoForm({ ...videoForm, platform: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Instagram">Instagram Reels</SelectItem>
                      <SelectItem value="YouTube Shorts">YouTube Shorts</SelectItem>
                      <SelectItem value="tiktok_coming_soon" disabled>TikTok (Coming soon)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Duration: {videoForm.duration}s</Label>
                  <Input
                    type="range"
                    min="15"
                    max="60"
                    step="5"
                    value={videoForm.duration}
                    onChange={(e) => setVideoForm({ ...videoForm, duration: parseInt(e.target.value) })}
                    className="cursor-pointer"
                  />
                </div>
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleGenerateVideo}
                  disabled={loading}
                  data-testid="generate-video-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Video className="w-4 h-4 mr-2" />}
                  Generate Script
                </Button>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Right Panel - Results */}
      <div className="flex-1 min-w-0">
        <Card className="border-zinc-200 h-full">
          <CardHeader className="pb-3 border-b border-zinc-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Generated Content</CardTitle>
              {generatedContent.length > 0 && (
                <span className="text-sm text-zinc-500">{generatedContent.length} items</span>
              )}
            </div>
            
            {/* Bulk Progress */}
            {bulkGenerating && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">
                    Generating... {bulkStats.generated}/{bulkStats.total} posts
                  </span>
                  <Button variant="outline" size="sm" onClick={cancelBulkGenerate}>
                    Cancel
                  </Button>
                </div>
                <Progress value={bulkProgress} className="h-2" />
                <p className="text-xs text-zinc-400">
                  This may take a few minutes for large batches
                </p>
              </div>
            )}
          </CardHeader>
          <ScrollArea className="h-[calc(100%-65px)]">
            <CardContent className="p-4">
              {loading && !bulkGenerating ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  </div>
                  <p className="text-zinc-600 font-medium">Generating your content...</p>
                  <p className="text-zinc-400 text-sm mt-1">This may take a moment</p>
                </div>
              ) : generatedContent.length === 0 && !bulkGenerating ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-zinc-400" />
                  </div>
                  <p className="text-zinc-600 font-medium">No content generated yet</p>
                  <p className="text-zinc-400 text-sm mt-1">Fill in the form and click generate</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {generatedContent.map((post, index) => (
                    <PostCard
                      key={post.id || index}
                      post={post}
                      onCopy={copyToClipboard}
                      onSave={toggleSave}
                      api={api}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </ScrollArea>
        </Card>
      </div>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Post Limit Reached
            </DialogTitle>
            <DialogDescription>
              You've reached your monthly post limit.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {subscription && (
              <div className="bg-zinc-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-500">Current Plan</span>
                  <span className="font-medium">{subscription.tier?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-500">Posts Used</span>
                  <span className="font-medium">
                    {subscription.posts_used} / {subscription.posts_limit === -1 ? '∞' : subscription.posts_limit}
                  </span>
                </div>
              </div>
            )}
            <p className="text-sm text-zinc-600">
              Upgrade your plan to generate more content and unlock additional features like bulk generation up to 365 posts.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>
              Maybe Later
            </Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/subscription');
              }}
              data-testid="upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PostCard = ({ post, onCopy, onSave, api }) => {
  const [expanded, setExpanded] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(post.generated_image || null);
  const [showImageModal, setShowImageModal] = useState(false);

  const getPlatformColor = (platform) => {
    const colors = {
      Instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
      LinkedIn: 'bg-[#0A66C2]',
      TikTok: 'bg-black',
      'Bluesky': 'bg-black',
      Facebook: 'bg-[#1877F2]',
      'YouTube Shorts': 'bg-[#FF0000]'
    };
    return colors[platform] || 'bg-zinc-500';
  };

  const copyAll = () => {
    const text = `${post.post_title}\n\n${post.caption}\n\n${post.hashtags}${post.video_script ? `\n\nScript:\n${post.video_script}` : ''}`;
    onCopy(text);
  };

  const handleGenerateImage = async () => {
    if (!post.image_prompt) {
      toast.error('No image prompt available');
      return;
    }

    setGeneratingImage(true);
    try {
      const response = await api.post('/generate/image', {
        prompt: post.image_prompt,
        post_id: post.id
      });
      setGeneratedImage(response.data.image_base64);
      toast.success('Image generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${generatedImage}`;
    link.download = `socialgen_${post.id || 'image'}.png`;
    link.click();
  };

  return (
    <>
      <div 
        className="p-4 rounded-xl border border-zinc-200 hover:border-zinc-300 transition-all bg-white"
        data-testid={`post-card-${post.id}`}
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <div className={`px-2 py-1 rounded-md text-white text-xs font-medium ${getPlatformColor(post.platform)}`}>
              {post.platform}
            </div>
            {post.post_type === 'carousel' && (
              <div className="px-2 py-1 rounded-md bg-violet-100 text-violet-700 text-xs font-medium">
                Carousel
              </div>
            )}
            {post.post_type === 'video_script' && (
              <div className="px-2 py-1 rounded-md bg-pink-100 text-pink-700 text-xs font-medium">
                Video Script
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={handleGenerateImage}
              disabled={generatingImage}
              title="Generate AI Image"
              data-testid={`generate-image-btn-${post.id}`}
            >
              {generatingImage ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ImageIcon className="w-4 h-4" />
              )}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={copyAll} data-testid={`copy-btn-${post.id}`}>
              <Copy className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={() => onSave(post)}
              data-testid={`save-btn-${post.id}`}
            >
              {post.is_saved ? (
                <BookmarkCheck className="w-4 h-4 text-indigo-600" />
              ) : (
                <Bookmark className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Generated Image Preview */}
        {generatedImage && (
          <div 
            className="mb-3 rounded-lg overflow-hidden cursor-pointer relative group bg-zinc-50 border border-zinc-200"
            onClick={() => setShowImageModal(true)}
          >
            <img 
              src={`data:image/png;base64,${generatedImage}`} 
              alt="Generated" 
              className="w-full max-h-[360px] object-contain mx-auto bg-zinc-50"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-sm">Click to expand</span>
            </div>
          </div>
        )}

        <h3 className="font-semibold text-zinc-900 mb-2">{post.post_title}</h3>
        <p className="text-sm text-zinc-600 mb-3">{post.caption}</p>

        <div className="flex flex-wrap gap-1 mb-3">
          {post.hashtags?.split(' ').filter(Boolean).map((tag, i) => (
            <span key={i} className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>

        <button
          className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          {expanded ? 'Hide details' : 'Show details'}
        </button>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-zinc-100 space-y-3">
            <div>
              <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-1">Image Prompt</p>
              <p className="text-sm text-zinc-600 bg-zinc-50 p-2 rounded-lg">{post.image_prompt}</p>
            </div>
            {post.video_script && (
              <div>
                <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-1">Video Script</p>
                <pre className="text-sm text-zinc-600 bg-zinc-50 p-2 rounded-lg whitespace-pre-wrap font-mono">
                  {post.video_script}
                </pre>
              </div>
            )}
            {post.carousel_content && post.carousel_content.length > 0 && (
              <div>
                <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">Carousel Slides</p>
                <div className="space-y-2">
                  {post.carousel_content.map((slide, i) => (
                    <div key={i} className="bg-zinc-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-xs font-medium flex items-center justify-center">
                          {slide.slide_number || i + 1}
                        </span>
                        <span className="font-medium text-zinc-900">{slide.headline}</span>
                      </div>
                      <p className="text-sm text-zinc-600 ml-8">{slide.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Generated Image</DialogTitle>
            <DialogDescription>{post.post_title}</DialogDescription>
          </DialogHeader>
          {generatedImage && (
            <div className="space-y-4">
              <img 
                src={`data:image/png;base64,${generatedImage}`} 
                alt="Generated" 
                className="w-full rounded-lg"
              />
              <div className="flex gap-2">
                <Button onClick={downloadImage} className="bg-indigo-600 hover:bg-indigo-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download Image
                </Button>
                <Button variant="outline" onClick={() => onCopy(post.image_prompt)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Prompt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
